/**
 * View component for /evaluations
 *
 * Generic evaluation list view. Defaults to 'all' with:
 * this.props.dispatch(evaluationActions.fetchListIfNeeded());
 *
 * NOTE: See /product/views/ProductList.js.jsx for more examples
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import actions
import * as evaluationActions from '../evaluationActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';

// import global components
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import { SearchInput } from '../../../global/components/forms';

// import evaluation components
import AdminEvaluationListItem from '../components/AdminEvaluationListItem.js.jsx';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';

// import evaluation css modules
import evaluationStyles from '../evaluationModuleStyles.css';

class AdminEvaluationList extends Base {
  constructor(props) {
    super(props);
    this.state = {
      perPage: 50
      , queryText: this.props.query || ''
    };
    this._bind(
      '_handleQuery'
      , '_handleSetPagination'
      , '_handleSort'
      , '_resetPagination'
      , '_setPerPage'
    );
  }

  componentDidMount() {
    const { dispatch } = this.props;
    // fetch a list of your choice
    dispatch(evaluationActions.fetchListIfNeeded()); // defaults to 'all'
    dispatch(evaluationActions.setFilter({query: '', sortBy: 'title'}));
    dispatch(evaluationActions.setPagination({page: 1, per: 50}));
    dispatch(contentModuleActions.fetchListIfNeeded());
  }

  _handleSort(sortBy) {
    const { dispatch, match, evaluationList } = this.props;
    let newFilter = evaluationList.filter;
    if(evaluationList.filter.sortBy && evaluationList.filter.sortBy.indexOf("-") < 0) {
      sortBy = "-" + sortBy;
    } else {
      sortBy = sortBy.substring(0)
    }
    newFilter.sortBy = sortBy;
    dispatch(evaluationActions.setFilter(newFilter));
  }

  _resetPagination() {
    const { dispatch, match } = this.props;
    dispatch(evaluationActions.setPagination({
      page: 1,
      per: parseInt(this.state.perPage)
    }));
  }

  _setPerPage(per) {
    const { dispatch, match, evaluationList } = this.props;
    let newPagination = evaluationList.pagination;
    newPagination.per = parseInt(per);
    newPagination.page = 1;
    dispatch(evaluationActions.setPagination(newPagination));
    this.setState({perPage: per});
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(evaluationActions.setPagination(newPagination));
  }

  _handleQuery(e) {
    const { dispatch, match, evaluationList } = this.props;
    // always defaulting the page to page 1 so we can see our results
    let pagination = {};
    pagination.page = 1;
    pagination.per = evaluationList.pagination.per;
    this._handleSetPagination(pagination);
    // continue query logic
    dispatch(evaluationActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: evaluationList.filter.sortBy
    }));
  }

  render() {
    const {
      evaluationList
      , evaluationStore
      , location
      , paginatedList
      , sortedList
      , contentModuleStore
    } = this.props;

    /**
     * Retrieve the list information and the list items for the component here.
     *
     * NOTE: if the list is deeply nested and/or filtered, you'll want to handle
     * these steps within the mapStoreToProps method prior to delivering the
     * props to the component.  Othwerwise, the render() action gets convoluted
     * and potentially severely bogged down.
     */

    const contentModuleList = contentModuleStore.util.getList() || [];

    /**
     * NOTE: isEmpty is is usefull when the component references more than one
     * resource list.
     */
    const isEmpty = !evaluationList || !evaluationList.items || evaluationList.didInvalidate;
    const sortedEmpty = sortedList.length === 0;

    return (
      <AuthoringLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <h1> Evaluation List </h1>
              <hr/>
              { isEmpty ?
                  (
                    evaluationList && evaluationList.isFetching ?
                      <div className="u-centerText">
                        <div className="loading"></div>
                      </div>
                    :
                      <h2>Empty.</h2>
                  )
                :
                  <div style={{ opacity: evaluationList.isFetching ? 0.5 : 1 }}>
                    <div className="yt-toolbar">
                      <div className="yt-tools right">
                        <div className="search">
                          <SearchInput
                            name="query"
                            value={evaluationList.filter.query}
                            change={this._handleQuery}
                            placeholder="Search..."
                            required={false}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="content-container">
                      <div className="table-wrapper">
                        <table className="yt-table striped">
                          <caption>
                            { evaluationList.filter && evaluationList.filter.query && evaluationList.filter.query.length > 0 ?
                              <span>Filtered Evaluations &mdash; {sortedList.length} of {Object.keys(evaluationList.items).length}</span>
                              :
                              <span>All Evaluations &mdash; {Object.keys(evaluationList.items).length}</span>
                            }
                            <div className="per-page-select u-pullRight">
                              <label>Show per page: </label>
                              <select
                                name="numPerPage"
                                onChange={(e) => this._setPerPage(e.target.value)}
                                value={this.state.perPage}
                                >
                                <option value={25}> 25 </option>
                                <option value={50}> 50 </option>
                                <option value={100}> 100 </option>
                              </select>
                            </div>
                          </caption>
                          <thead>
                            <tr>
                              <th className="_40 sortable" onClick={()=> this._handleSort('title')}>
                                Title
                                {evaluationList.filter.sortBy === 'title' ?
                                  <i className="ion ion-ios-arrow-down" />
                                  : evaluationList.filter.sortBy === '-title' ?
                                  <i className="ion ion-ios-arrow-up" />
                                  : null
                                }
                              </th>
                              <th className="_20">Content Module</th>
                              <th className="_20 sortable" onClick={()=> this._handleSort('status')}>
                                Status
                                {evaluationList.filter.sortBy === 'status' ?
                                  <i className="ion ion-ios-arrow-down" />
                                  : evaluationList.filter.sortBy === '-status' ?
                                  <i className="ion ion-ios-arrow-up" />
                                  : null
                                }
                              </th>
                              <th className="_20 sortable" onClick={()=> this._handleSort('updated')}>
                                Last Modified
                                {evaluationList.filter.sortBy === 'updated' ?
                                  <i className="ion ion-ios-arrow-down" />
                                  : evaluationList.filter.sortBy === '-updated' ?
                                  <i className="ion ion-ios-arrow-up" />
                                  : null
                                }
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {
                              paginatedList.map((evaluation, i) =>
                              <AdminEvaluationListItem
                                evaluation={evaluation}
                                key={evaluation._id}
                                contentModuleList={contentModuleList}
                                />
                            )
                          }
                        </tbody>
                      </table>
                      { sortedEmpty ?
                        null
                        :
                        <Pagination
                          pagination={evaluationList.pagination}
                          setPagination={this._handleSetPagination}
                          totalPages={ Math.floor(sortedList.length / evaluationList.pagination.per) + 1}
                          />
                      }
                    </div>
                    </div>
                  </div>
              }
            </div>
          </section>
        </div>
      </AuthoringLayout>
    )
  }
}

AdminEvaluationList.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  const evaluationStore = store.evaluation;
  const evaluationList = evaluationStore.lists ? evaluationStore.lists.all : null;
  let sortedList = [];
  let paginatedList = [];
  let filteredByQuery = [];
  if (evaluationList) {

    const pagination = evaluationList.pagination;
    const filter = evaluationList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'email';

    // FILTER BY QUERY
    let queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredByQuery = evaluationList && evaluationList.items ? evaluationList.items.filter((evaluationId) => {
      return filterUtils.filterEvaluation(queryTestString, evaluationStore.byId[evaluationId]);
    }) : []

    // SORT THE LIST
    sortedList = filteredByQuery.map((item) => {
      let newItem = evaluationStore.byId[item];
      return newItem;
    });
    switch(sortBy) {
      case 'title':
        sortedList = _.orderBy(sortedList, [item => item.title.toLowerCase()], ['asc']);
        break;
      case '-title':
        sortedList = _.orderBy(sortedList, [item => item.title.toLowerCase()], ['desc']);
        break;
      case 'status':
        sortedList = _.orderBy(sortedList, [item => item.status.toLowerCase()], ['asc']);
        break;
      case '-status':
        sortedList = _.orderBy(sortedList, [item => item.status.toLowerCase()], ['desc']);
        break;
      case 'updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['asc']);
        break;
      case '-updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['desc']);
        break;
      default:
        sortedList = _.orderBy(sortedList, [item => item.title.toLowerCase()], ['asc']);
    }

    // apply pagination
    let start = pagination ? (pagination.page - 1) * pagination.per : 1;
    let end = pagination ? start + pagination.per : 0;
    paginatedList = _.slice(sortedList, start, end);

  }
  return {
    evaluationList
    , evaluationStore
    , paginatedList
    , sortedList
    , contentModuleStore: store.contentModule
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminEvaluationList)
);



// WEBPACK FOOTER //
// modules/evaluation/views/AdminEvaluationList.js.jsx